import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { Label } from '@components/Label';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Tooltip } from '@components/tooltip/Tooltip';
import { ModalBody } from '@pages/customer/manager/userGroup/licenseRetrieve/ModalBody';
import { useStore } from '@stores/RootStore';
import { colors } from '@theme/colorsCustomer';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { DangerFillIcon } from '@icons/DangerFillIcon';
import type { SubscriptionUserUpdateRq } from '@repositories/subscriptionRepository/subscription.types';
import {
  useDeleteSubscriptionUser,
  useGetSubscriptionUser,
  useUpdateSubscriptionUser,
} from '@queryHooks/useSubscription';
import { useSubscriptionUserLicenses } from '@queryHooks/useSubscriptionUser';
import { SoftwareIcon, SoftwareLabel } from '../list/SubscriptionUserTable';
import { useSubscriptionUserForm } from './validationSchema';

type WarningInfo = {
  message: JSX.Element;
  icon: JSX.Element;
};

type StatusWarings = {
  [key: string]: WarningInfo;
};

const AbnormalStateWarningText = ({
  status,
  isMonthlyInactiveUser,
}: {
  status: string;
  isMonthlyInactiveUser?: boolean;
}) => {
  const statusWarnings: StatusWarings = {
    [t('Subscription_User_Mgmt.Normal_Member')]: {
      message: isMonthlyInactiveUser ? (
        <>
          <EmphasizeText>{t('User_Management.Warnings.One_Month_Inactive')}</EmphasizeText>&nbsp;
          {t('User_Management.Warnings.Login_Required')}&nbsp;
          <EmphasizeText>{t('User_Management.Warnings.Usage_Check')}</EmphasizeText>&nbsp;
          {t('User_Management.Warnings.Check_Required')}
        </>
      ) : (
        <>
          <EmphasizeText>{t('User_Management.Warnings.Inactive_Status')}</EmphasizeText>
          {t('User_Management.Warnings.Inactive_User')}&nbsp;
          <EmphasizeText>{t('User_Management.Warnings.Usage_Check')}</EmphasizeText>
          {t('User_Management.Warnings.Check_Required')}
        </>
      ),
      icon: <DangerFillIcon width={16} height={16} />,
    },
    [t('Subscription_User_Mgmt.Withdraw_Member')]: {
      message: (
        <>
          <EmphasizeText>{t('User_Management.Warnings.Withdraw_Member')}</EmphasizeText>
          {t('User_Management.Warnings.Is')}&nbsp;<EmphasizeText>{t('License_Retrieve.Title')}</EmphasizeText>
          {t('User_Management.Warnings.License_Retrieve_Required')}
        </>
      ),
      icon: <DangerFillIcon width={16} height={16} />,
    },
    [t('Subscrib_Detail_User_Dashboard_01')]: {
      message: (
        <>
          <EmphasizeText>{t('User_Management.Warnings.Non_Member_User')}</EmphasizeText>
          {t('User_Management.Warnings.Check_And_Manage')}&nbsp;
          <EmphasizeText>{t('User_Management.Warnings.License_Management')}</EmphasizeText>
          {t('User_Management.Warnings.Please_Manage')}
        </>
      ),
      icon: <DangerFillIcon width={16} height={16} />,
    },
  };
  const warning = statusWarnings[status];
  if (!warning) return null;

  return (
    <UserInfoWarning>
      {warning.icon}
      &nbsp;
      {warning.message}
    </UserInfoWarning>
  );
};

export const SubscriptionUserDetail = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const {
    state: { id, loginId, memberStatus, softwareIconUrl, softwareName, subscriptionName },
  }: {
    state: {
      id: string;
      loginId: string;
      memberStatus: string;
      softwareIconUrl: string;
      softwareName: string;
      subscriptionName: string;
    };
  } = useLocation();

  const [isMemoEditMode, setIsMemoEditMode] = useState<boolean>(true);
  const { reset, register, handleSubmit, watch } = useSubscriptionUserForm();

  const watchMemoLength = watch('memo')?.length;

  const { data: subscriptionUserDetail, isSuccess: getSubscriptionUserDetail } = useGetSubscriptionUser(
    id ?? '',
    loginId ?? '',
  );

  useEffect(() => {
    reset({
      memo: subscriptionUserDetail?.memo,
    });
  }, [reset, subscriptionUserDetail?.memo]);

  const today = dayjs();
  const isMonthlyInactiveUser = today.diff(dayjs(subscriptionUserDetail?.lastLoginTime), 'month') > 0;
  const isAbnormalStatus =
    memberStatus !== t('Subscription_User_Mgmt.Normal_Member') ||
    (memberStatus === t('Subscription_User_Mgmt.Normal_Member') && isMonthlyInactiveUser) ||
    (getSubscriptionUserDetail && subscriptionUserDetail?.userStatus !== 'ACTIVE');

  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  const { data: userLicensesData } = useSubscriptionUserLicenses(
    { loginId: subscriptionUserDetail?.userEmail ?? '' },
    { enabled: getSubscriptionUserDetail },
  );

  const currentLicense = userLicensesData?.content.find(
    license => license.subscription.id === subscriptionUserDetail?.subscriptionId,
  );
  const retrieveMode = currentLicense?.canRemoveUser ? 'auto' : 'manual';

  const { mutate: deleteUserFromSubscription } = useDeleteSubscriptionUser();
  const { mutate: updateSubscriptionUser, isPending: isUpdatePending } = useUpdateSubscriptionUser();

  const handleConfirmRetrieve = () => {
    deleteUserFromSubscription(
      {
        subscriptionId: subscriptionUserDetail?.subscriptionId ?? '',
        userLoginIdOrUserId:
          (subscriptionUserDetail?.userId ?? '') || (subscriptionUserDetail?.userLoginId ?? ''),
      },
      {
        onSuccess: () => {
          toastStore.open(t('License_Retrieve.Retrieve_Complete'));
        },
      },
    );
  };

  const handleClickRetrieve = () => {
    if (retrieveMode === 'auto') {
      alertStore.open({
        title: t('License_Retrieve.Title'),
        type: 'error',
        message: <ModalBody type="auto" />,
        confirmName: t('License_Retrieve.Retrieve'),
        cancelName: t('License_Retrieve.Cancel'),
        onConfirm: handleConfirmRetrieve,
        useCancel: true,
      });
    } else {
      alertStore.open({
        title: t('License_Retrieve.Complete_Modal_Title'),
        type: 'error',
        message: <ModalBody type="manual" />,
        confirmName: t('License_Retrieve.Complete_Button'),
        cancelName: t('License_Retrieve.Cancel'),
        onConfirm: handleConfirmRetrieve,
        useCancel: true,
      });
    }
  };

  const handleSubmitForm: SubmitHandler<SubscriptionUserUpdateRq> = formData => {
    const processRqData: SubscriptionUserUpdateRq = {
      memo: formData.memo,
      billableStatus: subscriptionUserDetail?.billableStatus ?? 'BILLABLE',
      lastLoginTime: subscriptionUserDetail?.lastLoginTime,
      userEmail: subscriptionUserDetail?.userEmail,
      userId: subscriptionUserDetail?.userId,
      userLoginId: subscriptionUserDetail?.userLoginId ?? '',
      userName: subscriptionUserDetail?.userName,
      userOrganization: subscriptionUserDetail?.userOrganization,
      userRole: subscriptionUserDetail?.userRole,
      userType: subscriptionUserDetail?.userType,
    };

    updateSubscriptionUser(
      { subscriptionId: subscriptionUserDetail?.subscriptionId ?? '', rqData: processRqData },
      {
        onSuccess: () => {
          setIsMemoEditMode(!isMemoEditMode);
          toastStore.open(t('User_Dialog.Save_Complete'));
        },
      },
    );
  };

  const handleEditMemo = () => {
    setIsMemoEditMode(!isMemoEditMode);
    reset({
      memo: subscriptionUserDetail?.memo,
    });
  };

  const handleClickSubscription = () => {
    navigate(`/manager/${tenantId}/subscriptions/list/${id}/user`);
  };

  return (
    <Container>
      {getSubscriptionUserDetail ? (
        <>
          <Header onClick={() => navigate(-1)}>
            <ChevronIcon rotateNum={270} />
            <HeaderTitle>{t('User_Management.User_List')}</HeaderTitle>
          </Header>
          <Body>
            <UserInfoWrapper>
              <UserInfoTitle>{t('User_Management.User_Info')}</UserInfoTitle>
              {isAbnormalStatus ? (
                <AbnormalStateWarningText
                  status={memberStatus}
                  isMonthlyInactiveUser={isMonthlyInactiveUser && subscriptionUserDetail.userStatus === 'ACTIVE'}
                />
              ) : null}
              <Divider isAbnormalStatus={isAbnormalStatus} />
              <UserInfoSection>
                <UserInfoDetailEl hasLabel>
                  <UserInfoDetailElName>{t('User_Management.Category')}</UserInfoDetailElName>
                  <UserInfoDetailElValue>
                    <Label
                      variant="solid"
                      color={
                        memberStatus === t('Subscription_User_Mgmt.Normal_Member')
                          ? 'green'
                          : memberStatus === t('Subscription_User_Mgmt.Withdraw_Member')
                          ? 'red'
                          : 'gray'
                      }
                    >
                      {t(memberStatus)}
                    </Label>
                  </UserInfoDetailElValue>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Name')}</UserInfoDetailElName>
                  <Tooltip label={subscriptionUserDetail.userName ?? '-'} visibleOnlyEllipsis placement="bottom-center">
                    <UserInfoDetailElValue>{subscriptionUserDetail.userName ?? '-'}</UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName />
                  <UserInfoDetailElValue />
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Account')}</UserInfoDetailElName>
                  <Tooltip
                    label={subscriptionUserDetail.user?.email ?? '-'}
                    visibleOnlyEllipsis
                    placement="bottom-center"
                  >
                    <UserInfoDetailElValue>{subscriptionUserDetail.user?.email ?? '-'}</UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Group')}</UserInfoDetailElName>
                  <Tooltip
                    label={subscriptionUserDetail.userGroup?.name ?? '-'}
                    visibleOnlyEllipsis
                    placement="bottom-center"
                  >
                    <UserInfoDetailElValue>{subscriptionUserDetail.userGroup?.name ?? '-'}</UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Contact')}</UserInfoDetailElName>
                  <Tooltip
                    label={subscriptionUserDetail.user?.phone ? subscriptionUserDetail.user?.phone : '-'}
                    visibleOnlyEllipsis
                    placement="bottom-center"
                  >
                    <UserInfoDetailElValue>
                      {subscriptionUserDetail.user?.phone ? subscriptionUserDetail.user?.phone : '-'}
                    </UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
              </UserInfoSection>
            </UserInfoWrapper>
            <UseSubscriptionListWrapper>
              <UseSubscriptionListTitle>{t('User_Management.Usage_Items')}</UseSubscriptionListTitle>
              <Divider style={{ marginTop: 0 }} />
              <UserInfoSection>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Product_Name')}</UserInfoDetailElName>
                  <Tooltip label={softwareName} visibleOnlyEllipsis placement="bottom-center">
                    <UserInfoDetailElValue>
                      <SoftwareLabel>
                        <SoftwareIcon src={softwareIconUrl} />
                        {softwareName}
                      </SoftwareLabel>
                    </UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Subscription_Name')}</UserInfoDetailElName>
                  <Tooltip label={subscriptionName} visibleOnlyEllipsis placement="bottom-center">
                    <UserInfoDetailElValue className="subscription" onClick={handleClickSubscription}>
                      {subscriptionName}
                    </UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName />
                  <UserInfoDetailElValue />
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Email_Account')}</UserInfoDetailElName>
                  <Tooltip
                    label={subscriptionUserDetail.userEmail ?? '-'}
                    visibleOnlyEllipsis
                    placement="bottom-center"
                  >
                    <UserInfoDetailElValue>{subscriptionUserDetail.userEmail ?? '-'}</UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Authority')}</UserInfoDetailElName>
                  <Tooltip label={subscriptionUserDetail.userRole ?? '-'} visibleOnlyEllipsis placement="bottom-center">
                    <UserInfoDetailElValue>{subscriptionUserDetail.userRole ?? '-'}</UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl>
                  <UserInfoDetailElName>{t('User_Management.Last_Used_Date')}</UserInfoDetailElName>
                  <Tooltip
                    label={
                      subscriptionUserDetail.lastLoginTime !== null
                        ? dayjs(subscriptionUserDetail.lastLoginTime).format('YYYY.MM.DD')
                        : '-'
                    }
                    visibleOnlyEllipsis
                    placement="bottom-center"
                  >
                    <UserInfoDetailElValue>
                      {subscriptionUserDetail.lastLoginTime !== null
                        ? dayjs(subscriptionUserDetail.lastLoginTime).format('YYYY.MM.DD')
                        : '-'}
                    </UserInfoDetailElValue>
                  </Tooltip>
                </UserInfoDetailEl>
                <UserInfoDetailEl hasLabel>
                  <UserInfoDetailElName>{t('User_Management.Billing_Status')}</UserInfoDetailElName>
                  <UserInfoDetailElValue>
                    <StyledLabel
                      variant="solid"
                      color={subscriptionUserDetail.billableStatus === 'BILLABLE' ? 'white' : 'red'}
                    >
                      {subscriptionUserDetail.billableStatus === 'BILLABLE' ? 'Y' : 'N'}
                    </StyledLabel>
                  </UserInfoDetailElValue>
                </UserInfoDetailEl>
                <UserInfoDetailEl hasLabel>
                  <UserInfoDetailElName>{t('User_Management.Status')}</UserInfoDetailElName>
                  <UserInfoDetailElValue>
                    <Label variant="solid" color={subscriptionUserDetail.userStatus === 'ACTIVE' ? 'blue' : 'gray'}>
                      {subscriptionUserDetail.userStatus === 'ACTIVE'
                        ? t('Subscription.userStatus.ACTIVE')
                        : t('Subscription.userStatus.INACTIVE')}
                    </Label>
                  </UserInfoDetailElValue>
                </UserInfoDetailEl>
                <UserInfoDetailEl memberStatus={memberStatus}>
                  <UserInfoDetailElName>{t('User_Management.Management')}</UserInfoDetailElName>
                  <UserInfoDetailElValue className="licenseButton" onClick={handleClickRetrieve}>
                    {t('License_Retrieve.Title')}
                  </UserInfoDetailElValue>
                </UserInfoDetailEl>
              </UserInfoSection>
              <UserMemoSection>
                <UpdateMemoForm onSubmit={handleSubmit(handleSubmitForm)}>
                  <UserMemoHeader>
                    <UserMemoTitle>
                      {t('ExtraInfo.memo.title')}
                      {!isMemoEditMode ? (
                        <TextCountBox>
                          ( <TextCount>{watchMemoLength}</TextCount> / 5000 )
                        </TextCountBox>
                      ) : null}
                    </UserMemoTitle>
                    {isMemoEditMode ? (
                      <UserMemoButton variant="outlined" size="small" onClick={handleEditMemo}>
                        {t('Common.button.edit')}
                      </UserMemoButton>
                    ) : (
                      <UserMemoBox>
                        <SaveButton variant="outline" size="small" type="submit" isPending={isUpdatePending}>
                          {t('Common.button.save')}
                        </SaveButton>
                        <UserMemoButton variant="outlined" size="small" onClick={handleEditMemo}>
                          {t('Common.button.cancel')}
                        </UserMemoButton>
                      </UserMemoBox>
                    )}
                  </UserMemoHeader>
                  <UserMemo
                    {...register('memo')}
                    placeholder={t('ExtraInfo.memo.placeholder')}
                    disabled={isMemoEditMode}
                    maxLength={5000}
                  />
                </UpdateMemoForm>
              </UserMemoSection>
            </UseSubscriptionListWrapper>
          </Body>
        </>
      ) : null}
    </Container>
  );
};

const Container = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  padding: 29px 32px;
  overflow: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${fonts.Headline6};
  color: ${props => props.theme.colors['text-gray-main']};
  margin-bottom: 40px;
  gap: 4px;
  cursor: pointer;
`;

const HeaderTitle = styled.strong``;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 900px;
`;

const UserInfoWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0px 24px;
`;

const UserInfoTitle = styled.span`
  ${fonts.Headline7};
  color: ${props => props.theme.colors['text-gray-main']};
`;

const UserInfoWarning = styled.div`
  ${fonts.Body2}
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 16px;
  width: 100%;
  height: fit-content;
  padding: 18px 16px;
  background-color: ${props => props.theme.colors['bg-red-lighter']};
  border: 1px solid ${props => props.theme.colors['border-red-lighter-more']};
  border-radius: 5px;
  color: ${props => props.theme.colors['text-gray-main']};
`;

const Divider = styled.hr<{ isAbnormalStatus?: boolean }>`
  margin-top: ${props => (props.isAbnormalStatus ? '12' : '16')}px;
  width: 100%;
  border: 0;
  height: 1px;
  background: ${props => props.theme.colors['border-gray-light']};
  margin-bottom: 0;
`;

const UserInfoSection = styled.section`
  width: 900px;
  display: grid;
  grid-template-columns: repeat(3, 300px);
  grid-template-rows: repeat(2, 72px);

  & :nth-of-type(3n) {
    border-right: none;
  }
`;

const UserInfoDetailEl = styled.div<{ hasLabel?: boolean; memberStatus?: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${props => (props.hasLabel ? '4' : '2')}px;
  padding: 14px 20px ${props => (props.hasLabel ? '12' : '14')}px 20px;
  box-sizing: border-box;

  border-right: 1px solid ${colors.light['border-gray-w-lighter']};
  border-bottom: 1px solid ${colors.light['border-gray-w-lighter']};
  & .licenseButton {
    ${fonts.Headline8}
    cursor: pointer;
    color: ${colors.light['text-blue-light']};
  }
  & .licenseButton:hover {
    font: ${fonts.Underline3};
  }

  & .subscription {
    color: ${colors.light['text-blue-light']};
  }

  & .subscription:hover {
    ${fonts.Underline3}
    cursor: pointer;
  }
`;

const UserInfoDetailElName = styled.span`
  ${fonts.Headline9};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;

const UserInfoDetailElValue = styled.span`
  height: 20px;
  ${fonts.Headline8};
  color: ${props => props.theme.colors['text-gray-main']};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const UseSubscriptionListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 72px 24px 0px 24px;
  width: 900px;
`;

const UseSubscriptionListTitle = styled.span`
  ${fonts.Headline7};
  color: ${props => props.theme.colors['text-gray-main']};
  margin-bottom: 16px;
`;

const StyledLabel = styled(Label)`
  ${fonts.Caption5}
  background-color: ${colors.light['bg-white']};
`;

const UserMemoSection = styled.section`
  width: 750px;
  display: flex;
  flex-direction: column;
`;
const UserMemoHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: 76px 0px 10px 0;
`;

const UserMemoTitle = styled.strong`
  font: ${fonts.Headline7};
  display: flex;
  gap: 6px;
  align-items: center;
`;

const UserMemoBox = styled.div`
  display: flex;
  gap: 8px;
`;

const UserMemoButton = styled(Button)`
  font: ${fonts.Button4};
  padding: 8px 12px;
  min-width: 56px;
`;

const SaveButton = styled(SecureSubmitButton)`
  ${fonts.Button4}
  padding: 8px 12px;
  min-width: 56px;
`;

const UserMemo = styled.textarea`
  ${fonts.Body2}
  width: 750px;
  height: 192px;
  display: flex;
  padding: 6px 12px;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 2px;
  border: 1px solid ${colors.light['border-gray-lighter']};
  resize: none;
  background-color: ${colors.light['bg-gray-lighter']};
  ::placeholder {
    color: ${colors.light['text-gray-light']};
  }
`;

const TextCountBox = styled.span`
  ${fonts.Body2}
  color: ${colors.light['text-gray-light']};
`;
const TextCount = styled.span`
  color: ${colors.light['text-primary-light']};
`;

const UpdateMemoForm = styled.form``;

const EmphasizeText = styled.span`
  color: ${props => props.theme.colors['text-red']};
`;
